import React, {useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import AccountBalanceSharpIcon from '@mui/icons-material/AccountBalanceSharp';
import MapIcon from '@mui/icons-material/Map';
import Typography from '@mui/material/Typography';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Link } from 'react-router-dom';
import { Difficulty } from "../../shared/Difficulty";
import { getUserStats, getRecentChallenges, getLastSolved } from "../../api/Api";
import { categoryToText } from "../../utils/Category";
import SkillRadar, { buildSkillRadarData } from './SkillRadar';
import SeasonCard from './SeasonCard';
import ActivityCard from './ActivityCard';
import DiscordCTA from '../../shared/DiscordCTA';
import EmailOptInPrompt from '../../shared/EmailOptInPrompt';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import StatCard from '../../shared/StatCard';
import ProgressRing from '../../shared/ProgressRing';
import StatusBadge from '../../shared/StatusBadge';
import { useColors } from '../../theme/ColorModeContext';

const ranks = [
  { value: 0, label: "Novice", },
  { value: 20, label: "Explorer", },
  { value: 40, label: "Master", },
  { value: 60, label: "Hacker", },
  { value: 80, label: "Sentinel"},
  { value: 100, label: "Overlord", },
]

const rankToText = (rank: number) => {
    if(rank < 20) return ranks[0].label;
    else if(rank < 40) return ranks[1].label;
    else if(rank < 60) return ranks[2].label;
    else if(rank < 80) return ranks[3].label;
    else if(rank < 100) return ranks[4].label;
    else return ranks[5].label;
};

const getNextRank = (currentRank: string): { name: string; threshold: number } | null => {
    const order = ['novice', 'explorer', 'master', 'hacker', 'sentinel', 'overlord'];
    const thresholds = [0, 20, 40, 60, 80, 100];
    const idx = order.indexOf(currentRank.toLowerCase());
    if (idx < 0 || idx >= order.length - 1) return null;
    return { name: ranks[idx + 1].label, threshold: thresholds[idx + 1] };
};

interface Challenge {
    id: string;
    name: string;
    type: number;
    category: string;
    difficulty: number;
    points: number;
    solved: boolean;
}

interface Stats {
  category: string;
  challenges: number;
  solved: number;
}

interface UserStats {
  username: string;
  points: number;
  total: number;
  stats: Stats[];
}

interface LastSolved {
  user_id: string;
  username: string;
  challenge_id: string;
  challenge_name: string;
  challenge_difficulty: number;
  challenge_type: number;
}

interface PropsDashboard {
    token: string;
}

const Dashboard = (props: PropsDashboard) => {
    const colors = useColors();
    const token = props.token;

    const rankColors: Record<string, string> = {
        novice: colors.rank.novice,
        explorer: colors.rank.explorer,
        master: colors.rank.master,
        hacker: colors.rank.hacker,
        sentinel: colors.rank.sentinel,
        overlord: colors.rank.overlord,
    };

    const [userStats, setUserStats] = useState<UserStats>();

    const [missions, setMissions] = useState<Stats>();
    const [rank, setRank] = useState("Novice");

    const [challenges, setChallenges] = useState<Challenge[]>([]);
    const [lastSolved, setLastSolved] = useState<LastSolved[]>([]);
    const [totalSolved, setTotalSolved] = useState(0);
    const [totalChallenges, setTotalChallenges] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getRecentChallenges(token, 5);
            if(data) {
                setChallenges(data.challenges);
            }
        }

        const fetchUserStats = async () => {
          if(!token) return;

          const response = await getUserStats(token);
          if(response) {
            setUserStats(response.data);
            const tmpRank = rankToText(response.data.points * 100 / response.data.total);
            setRank(tmpRank);

            const data = response.data;
            let solved = 0;
            let total = 0;
            for(let i=0; i<data.stats.length; i++) {
              solved += data.stats[i].solved;
              total += data.stats[i].challenges;
              if(data.stats[i].category === "season1" || data.stats[i].category === "missions") setMissions(data.stats[i]);
            }
            setTotalSolved(solved);
            setTotalChallenges(total);
          }
        }

        const fetchLastSolved = async () => {
          const data = await getLastSolved(token);
          if(data) {
              setLastSolved(data.solved_challenges);
          }
        };

        fetchUserStats();
        fetchData();
        fetchLastSolved();
    }, []);

    const rankColor = rankColors[rank.toLowerCase()] || colors.text.secondary;
    const completionPct = totalChallenges > 0 ? Math.round((totalSolved / totalChallenges) * 100) : 0;
    const nextRank = getNextRank(rank);
    const radarData = buildSkillRadarData(userStats?.stats);

    return (
        <PageContainer>
            {/* Renders nothing unless the user has yet to make a choice about
                product-update emails. */}
            <EmailOptInPrompt token={token} placement="dashboard" />

            {/* Quick Stats Row */}
            <Grid container spacing={2} sx={{ marginBottom: '24px' }}>
                <Grid item xs={6} md={3}>
                  <StatCard
                    icon={<StarIcon />}
                    value={userStats ? userStats.points : 0}
                    label="Total Points"
                    accentColor={colors.accent.cyan}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    icon={<CheckCircleIcon />}
                    value={totalSolved}
                    label="Challenges Solved"
                    accentColor={colors.accent.green}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    icon={<EmojiEventsIcon />}
                    value={rank}
                    label="Current Rank"
                    accentColor={rankColor}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    icon={<TrendingUpIcon />}
                    value={`${completionPct}%`}
                    label="Completion"
                    accentColor={colors.accent.purple}
                  >
                    <ProgressRing value={completionPct} size={44} strokeWidth={3} showLabel={false} />
                  </StatCard>
                </Grid>
              </Grid>

              {/* Streak + 26-week calendar: the reason to come back tomorrow */}
              <ActivityCard token={token} />

              <GlowCard sx={{ padding: 0 }} glowOnHover={false}>
                <Grid container spacing={6} sx={{ padding: '30px 40px' }}>
                  {/* Row 1: Skill radar | Profile */}
                  <Grid item xs={12} md={6}>
                    <SkillRadar data={radarData} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* Header row: username + rank badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                      <Typography variant="h4" sx={{ fontFamily: '"Orbitron", sans-serif' }}>
                        {userStats && userStats.username}
                      </Typography>
                      <Box sx={{
                        px: '10px', py: '4px',
                        border: `1px solid ${rankColor}`,
                        borderRadius: '6px',
                        backgroundColor: `${rankColor}18`,
                        boxShadow: `0 0 10px ${rankColor}33`,
                      }}>
                        <Typography sx={{
                          fontFamily: '"Share Tech Mono", monospace',
                          fontSize: '11px',
                          fontWeight: 700,
                          color: rankColor,
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                        }}>
                          {rank}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Rank avatar + stats */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                      {/* Avatar */}
                      <Box sx={{
                        flexShrink: 0,
                        borderRadius: '50%',
                        padding: '3px',
                        background: `linear-gradient(135deg, ${rankColor}, transparent)`,
                        animation: 'glowPulse 4s ease-in-out infinite',
                        boxShadow: `0 0 24px ${rankColor}44`,
                      }}>
                        <img style={{
                          width: '130px',
                          height: '130px',
                          borderRadius: '50%',
                          border: `2px solid ${rankColor}`,
                          display: 'block',
                        }} src={`/images/ranks/${rank.toLowerCase()}.png`} alt={rank} />
                      </Box>

                      {/* Stats */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* 2×2 stat grid */}
                        <Grid container spacing={1} sx={{ marginBottom: '14px' }}>
                          {[
                            { label: 'Points', value: userStats ? userStats.points : 0, color: colors.accent.cyan },
                            { label: 'Solved', value: totalSolved, color: colors.accent.green },
                            { label: 'Challenges', value: totalChallenges, color: colors.accent.purple },
                            { label: 'Completion', value: `${completionPct}%`, color: rankColor },
                          ].map(stat => (
                            <Grid item xs={6} key={stat.label}>
                              <Box sx={{
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: `1px solid ${colors.border.subtle}`,
                                backgroundColor: `${stat.color}0a`,
                              }}>
                                <Typography sx={{
                                  fontFamily: '"Share Tech Mono", monospace',
                                  fontSize: '16px',
                                  fontWeight: 700,
                                  color: stat.color,
                                  lineHeight: 1,
                                }}>
                                  {stat.value}
                                </Typography>
                                <Typography sx={{
                                  fontFamily: '"Share Tech Mono", monospace',
                                  fontSize: '9px',
                                  color: colors.text.muted,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.08em',
                                  marginTop: '3px',
                                }}>
                                  {stat.label}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Rank progress bar */}
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                            <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '9px', color: colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              {rank}
                            </Typography>
                            {nextRank ? (
                              <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '9px', color: colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                {nextRank.name}: {Math.max(0, Math.round(nextRank.threshold * (userStats?.total ?? 0) / 100) - (userStats?.points ?? 0))} pts
                              </Typography>
                            ) : (
                              <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '9px', color: colors.accent.cyan, letterSpacing: '0.08em' }}>
                                MAX RANK
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ height: '4px', borderRadius: '2px', backgroundColor: `${rankColor}22`, overflow: 'hidden' }}>
                            <Box sx={{
                              height: '100%',
                              width: `${userStats ? (userStats.points * 100 / userStats.total) : 0}%`,
                              borderRadius: '2px',
                              background: `linear-gradient(90deg, ${rankColor}, ${colors.accent.cyan})`,
                              boxShadow: `0 0 8px ${rankColor}88`,
                              transition: 'width 1s ease',
                            }} />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Row 2: Missions | Rank Path, same Grid row = same vertical start */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: colors.border.subtle }} />
                      <Typography variant="overline" sx={{
                        color: colors.text.muted,
                        fontSize: '10px',
                        letterSpacing: '0.12em',
                        whiteSpace: 'nowrap',
                      }}>
                        Missions
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: colors.border.subtle }} />
                    </Box>
                    {missions && (
                      <SeasonCard season={1} solved={missions.solved} total={missions.challenges} />
                    )}
                    <DiscordCTA
                      variant="tile"
                      placement="dashboard-missions"
                      body="Team up, compare runs, and talk strategy on Discord."
                      sx={{ marginTop: '10px' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* Rank progression cards: mission-style tiles */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${rankColor}44, transparent)` }} />
                      <Typography sx={{
                        fontFamily: '"Share Tech Mono", monospace',
                        fontSize: '9px',
                        color: colors.text.muted,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}>
                        Rank Path
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(270deg, ${rankColor}44, transparent)` }} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: '6px' }}>
                      {ranks.map((r, idx) => {
                        const rColor = rankColors[r.label.toLowerCase()] || colors.text.secondary;
                        const userPct = userStats ? userStats.points * 100 / userStats.total : 0;
                        const isAchieved = userPct >= r.value;
                        const isCurrent = r.label.toLowerCase() === rank.toLowerCase();
                        return (
                          <Box key={r.label} sx={{
                            flex: 1,
                            position: 'relative',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: isCurrent
                              ? `2px solid ${rColor}`
                              : isAchieved
                                ? `1px solid ${rColor}55`
                                : `1px solid ${colors.border.subtle}`,
                            boxShadow: isCurrent ? `0 0 14px ${rColor}55` : 'none',
                            transition: 'all 0.3s ease',
                            opacity: isAchieved ? 1 : 0.45,
                            '&:hover': { opacity: 1, transform: 'translateY(-2px)' },
                          }}>
                            {/* Rank image */}
                            <Box sx={{
                              paddingTop: '100%',
                              backgroundImage: `url("/images/ranks/${r.label.toLowerCase()}.png")`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              position: 'relative',
                            }}>
                              {/* Gradient overlay */}
                              <Box sx={{
                                position: 'absolute',
                                inset: 0,
                                background: isCurrent
                                  ? `linear-gradient(180deg, transparent 30%, ${rColor}33 100%)`
                                  : `linear-gradient(180deg, transparent 30%, rgba(10,14,23,0.7) 100%)`,
                              }} />
                              {/* Current badge */}
                              {isCurrent && (
                                <Box sx={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  backgroundColor: rColor,
                                  borderRadius: '3px',
                                  px: '3px',
                                  py: '1px',
                                }}>
                                  <Typography sx={{ fontSize: '7px', fontWeight: 800, color: '#000', fontFamily: '"Share Tech Mono", monospace', lineHeight: 1 }}>
                                    NOW
                                  </Typography>
                                </Box>
                              )}
                              {/* Achieved checkmark */}
                              {isAchieved && !isCurrent && (
                                <Box sx={{ position: 'absolute', top: '4px', right: '4px' }}>
                                  <CheckCircleIcon sx={{ fontSize: '12px', color: rColor, filter: `drop-shadow(0 0 3px ${rColor})` }} />
                                </Box>
                              )}
                            </Box>
                            {/* Label */}
                            <Box sx={{
                              textAlign: 'center',
                              padding: '4px 2px',
                              backgroundColor: isCurrent ? `${rColor}18` : 'transparent',
                            }}>
                              <Typography sx={{
                                fontFamily: '"Share Tech Mono", monospace',
                                fontSize: '8px',
                                fontWeight: isCurrent ? 700 : 400,
                                color: isCurrent ? rColor : colors.text.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                lineHeight: 1.2,
                              }}>
                                {r.label}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Grid>
                </Grid>
              </GlowCard>

            <Grid container spacing={3} sx={{ marginTop: '16px' }}>
                {/* Latest Hacks */}
                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <Box sx={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      backgroundColor: colors.accent.green,
                      boxShadow: `0 0 8px ${colors.accent.green}`,
                      animation: 'glowPulse 2s ease-in-out infinite',
                    }} />
                    <Typography variant="h5" sx={{
                      fontFamily: '"Orbitron", sans-serif',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      fontSize: '14px',
                      color: colors.text.secondary,
                    }}>
                      Latest Hacks
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${colors.accent.cyanDim}44, transparent)` }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    {lastSolved.map((solved, idx) => (
                      <Box key={idx} sx={{
                        position: 'relative',
                        backgroundColor: colors.bg.secondary,
                        border: `1px solid ${colors.border.subtle}`,
                        borderLeft: `2px solid ${solved.challenge_type === 1 ? colors.accent.purple : colors.accent.cyan}`,
                        borderRadius: '8px',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        overflow: 'hidden',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          backgroundColor: colors.bg.tertiary,
                          borderColor: solved.challenge_type === 1 ? colors.accent.purple : colors.accent.cyan,
                          boxShadow: `0 0 16px ${solved.challenge_type === 1 ? colors.accent.purpleGlow : colors.accent.cyanGlow}`,
                          transform: 'translateX(3px)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0, left: 0, right: 0, bottom: 0,
                          background: solved.challenge_type === 1
                            ? `linear-gradient(90deg, ${colors.accent.purpleGlow} 0%, transparent 40%)`
                            : `linear-gradient(90deg, ${colors.accent.cyanGlow} 0%, transparent 40%)`,
                          opacity: 0.5,
                          pointerEvents: 'none',
                        },
                      }}>
                        {/* Index */}
                        <Typography sx={{
                          fontFamily: '"Share Tech Mono", monospace',
                          fontSize: '10px',
                          color: solved.challenge_type === 1 ? colors.accent.purple : colors.accent.cyan,
                          opacity: 0.7,
                          minWidth: '20px',
                          userSelect: 'none',
                        }}>
                          {String(idx + 1).padStart(2, '0')}
                        </Typography>

                        {/* Type icon */}
                        <Box sx={{ color: solved.challenge_type === 1 ? colors.accent.purple : colors.accent.cyan, display: 'flex', alignItems: 'center' }}>
                          {solved.challenge_type === 0
                            ? <AccountBalanceSharpIcon sx={{ fontSize: '14px' }} />
                            : <MapIcon sx={{ fontSize: '14px' }} />}
                        </Box>

                        {/* User → Challenge */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <Link style={{ textDecoration: 'none' }} to={`/profile/${solved.username}`}>
                              <Typography sx={{
                                fontFamily: '"Orbitron", sans-serif',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: colors.text.primary,
                                '&:hover': { color: colors.accent.cyan },
                                transition: 'color 0.2s',
                              }}>
                                {solved.username}
                              </Typography>
                            </Link>
                            <Typography sx={{ fontSize: '10px', color: colors.text.muted, fontFamily: '"Share Tech Mono", monospace' }}>
                              ▶
                            </Typography>
                            <Link style={{ textDecoration: 'none' }} to={solved.challenge_type === 1 ? `/missions/${solved.challenge_id}` : `/academy/challenge/${solved.challenge_id}`}>
                              <Typography sx={{
                                fontFamily: '"Share Tech Mono", monospace',
                                fontSize: '12px',
                                color: colors.text.secondary,
                                '&:hover': { color: colors.accent.cyan },
                                transition: 'color 0.2s',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {solved.challenge_name}
                              </Typography>
                            </Link>
                          </Box>
                        </Box>

                        {/* Difficulty */}
                        <Difficulty level={solved.challenge_difficulty} />
                      </Box>
                    ))}
                  </Box>
                </Grid>

                {/* Latest Challenges */}
                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <Box sx={{
                      width: '6px', height: '6px', borderRadius: '50%',
                      backgroundColor: colors.accent.purple,
                      boxShadow: `0 0 8px ${colors.accent.purple}`,
                      animation: 'glowPulse 2s ease-in-out infinite',
                    }} />
                    <Typography variant="h5" sx={{
                      fontFamily: '"Orbitron", sans-serif',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      fontSize: '14px',
                      color: colors.text.secondary,
                    }}>
                      Latest Challenges
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${colors.accent.purple}44, transparent)` }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    {challenges.map((challenge, idx) => (
                      <Link
                        key={idx}
                        style={{ textDecoration: 'none' }}
                        to={challenge.type === 0 ? `/academy/challenge/${challenge.id}` : `/missions/${challenge.id}`}
                      >
                        <Box sx={{
                          position: 'relative',
                          backgroundColor: colors.bg.secondary,
                          border: `1px solid ${colors.border.subtle}`,
                          borderLeft: `2px solid ${challenge.type === 1 ? colors.accent.purple : colors.accent.cyan}`,
                          borderRadius: '8px',
                          padding: '10px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          overflow: 'hidden',
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            backgroundColor: colors.bg.tertiary,
                            borderColor: challenge.type === 1 ? colors.accent.purple : colors.accent.cyan,
                            boxShadow: `0 0 16px ${challenge.type === 1 ? colors.accent.purpleGlow : colors.accent.cyanGlow}`,
                            transform: 'translateX(3px)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            background: challenge.type === 1
                              ? `linear-gradient(90deg, ${colors.accent.purpleGlow} 0%, transparent 40%)`
                              : `linear-gradient(90deg, ${colors.accent.cyanGlow} 0%, transparent 40%)`,
                            opacity: 0.5,
                            pointerEvents: 'none',
                          },
                        }}>
                          {/* Index */}
                          <Typography sx={{
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: '10px',
                            color: challenge.type === 1 ? colors.accent.purple : colors.accent.cyan,
                            opacity: 0.7,
                            minWidth: '20px',
                            userSelect: 'none',
                          }}>
                            {String(idx + 1).padStart(2, '0')}
                          </Typography>

                          {/* Type icon */}
                          <Box sx={{ color: challenge.type === 1 ? colors.accent.purple : colors.accent.cyan, display: 'flex', alignItems: 'center' }}>
                            {challenge.type === 0
                              ? <AccountBalanceSharpIcon sx={{ fontSize: '14px' }} />
                              : <MapIcon sx={{ fontSize: '14px' }} />}
                          </Box>

                          {/* Name */}
                          <Typography sx={{
                            fontFamily: '"Orbitron", sans-serif',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: colors.text.primary,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {challenge.name}
                          </Typography>

                          {/* Category tag */}
                          <Box sx={{
                            px: '6px', py: '2px',
                            border: `1px solid ${colors.border.subtle}`,
                            borderRadius: '4px',
                            flexShrink: 0,
                          }}>
                            <Typography sx={{
                              fontFamily: '"Share Tech Mono", monospace',
                              fontSize: '9px',
                              color: colors.text.muted,
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                            }}>
                              {categoryToText(challenge.category)}
                            </Typography>
                          </Box>

                          {/* Difficulty */}
                          <Difficulty level={challenge.difficulty} />

                          {/* Points */}
                          <Typography sx={{
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: '11px',
                            color: colors.accent.cyan,
                            minWidth: '36px',
                            textAlign: 'right',
                            flexShrink: 0,
                          }}>
                            +{challenge.points}
                          </Typography>

                          {/* Solved */}
                          <StatusBadge solved={challenge.solved} />
                        </Box>
                      </Link>
                    ))}
                  </Box>
                </Grid>
            </Grid>
        </PageContainer>
    );
}

export default Dashboard;
